import React from 'react';
import './LandingPage.css';
import {
  PLANS,
  PUBLIC_PLAN_ORDER,
  ADD_ONS,
} from '../settings/ManageCompany/wizardConfig';
import { Check } from './icons';

/**
 * The pricing section — plan cards, optional add-ons and the Compare Plans
 * table — shared by the public landing page and Profile > Manage
 * subscription, so a customer switching plans sees exactly what the
 * landing page sells. Everything is read from wizardConfig's rate card,
 * which planCatalogRuntime keeps in step with Plans & Pricing; the caller
 * loads the catalog (ensurePlanCatalog) and re-renders when it lands.
 *
 * Must be rendered inside `.nv-landing` — every style is scoped under it.
 * The in-app screen adds `nv-embedded` to drop the full-page chrome.
 *
 * The defaults reproduce the landing page. In-app callers swap the "Get
 * Started" button per card (`renderAction`), add a note under the price
 * (`renderPriceNote`), a badge by the name (`renderNameBadge`) and an extra
 * card class (`cardClassName`), and drop the two marketing blocks below the
 * cards (`showAddOns`, `showCompare`).
 */

// Add-ons sold on the public (Global) site: Alpha-only add-ons can exist in
// the catalog but are never advertised, and withdrawn ones (active: false)
// aren't either. Computed on every render, not once at import: the live rate
// card arrives after this module is evaluated.
function globalAddOns() {
  return ADD_ONS.filter((a) => a.global !== false && a.active !== false);
}

// Compare-plans rows derived from the SAME plan sections Create Company
// renders, so the table always matches the plans/features exactly. A plan
// "has" a category when it defines a section with that title; the cell note
// is the section's own note ("All Features Included") when present,
// otherwise just a check.
function compareCategories(order) {
  const titles = [];
  order.forEach((pid) => {
    (PLANS[pid]?.sections || []).forEach((sec) => {
      if (!titles.includes(sec.title)) titles.push(sec.title);
    });
  });
  return titles;
}

// The features that define each category, gathered from every plan that spells
// the category out. Lets us spot a plan that ships those features under a
// different heading: a tier whose sections collapse to "All Features Included"
// still covers the category.
function compareCategoryItems(order) {
  const map = {};
  order.forEach((pid) => {
    (PLANS[pid]?.sections || []).forEach((sec) => {
      if (!sec.items || sec.items.length === 0) return;
      map[sec.title] = map[sec.title] || [];
      sec.items.forEach((item) => {
        if (!map[sec.title].includes(item)) map[sec.title].push(item);
      });
    });
  });
  return map;
}

// Every feature a plan lists, across all of its sections.
function planFeatures(pid) {
  const all = [];
  (PLANS[pid]?.sections || []).forEach((sec) => {
    (sec.items || []).forEach((item) => {
      if (!all.includes(item)) all.push(item);
    });
  });
  return all;
}

function planCmp(pid, title, categoryItems) {
  const sec = (PLANS[pid]?.sections || []).find((s) => s.title === title);
  if (sec) return sec.note ? sec.note : true;
  // No section with that title, but the plan may still ship the whole category
  // under another heading — count it only when every feature is covered.
  const needed = categoryItems[title] || [];
  if (needed.length === 0) return false;
  const owned = planFeatures(pid);
  return needed.every((item) => owned.includes(item));
}

function compareRows(order) {
  const categoryItems = compareCategoryItems(order);
  return [
    ...compareCategories(order).map((title) => ({
      f: title,
      cells: order.map((pid) => planCmp(pid, title, categoryItems)),
    })),
    // Marketing row (not a plan feature category) — every plan includes it.
    {
      f: '24/7 Support',
      cells: order.map(() => true),
    },
  ];
}

// Comparison-table cell: true → check; string → check + note; false → dash.
function renderCmpCell(val) {
  if (val === true) {
    return (
      <span className="nv-yes">
        <Check />
      </span>
    );
  }
  if (val === false) {
    return <span className="nv-no">—</span>;
  }
  return (
    <>
      <span className="nv-yes">
        <Check />
      </span>
      <div className="nv-note">{val}</div>
    </>
  );
}

export default function PricingPlans({
  id = 'nv-pricing',
  planOrder: planOrderProp,
  eyebrow = 'Pricing',
  title = 'Simple, Transparent Pricing',
  subtitle = 'Pick a plan, scale as you grow.',
  showFreeTrial = true,
  // The marketing rate card below the cards. Off in-app: Manage
  // subscription picks add-ons per plan, on the card being switched to,
  // so a second read-only price list beside it is noise.
  showAddOns = true,
  // The comparison table. Off in-app for the same reason — a customer
  // choosing between two plans they can see side by side doesn't need the
  // shopping-page grid, and the cards already list what each includes.
  showCompare = true,
  onGetStarted,
  renderAction,
  // Replaces the whole price block. The in-app screen uses it to lead with
  // what THIS company pays and strike through the list price, which the
  // public page must not do — a visitor has no negotiated rate to compare
  // against. Returning null falls back to the default sticker price.
  renderPrice,
  renderPriceNote,
  renderNameBadge,
  cardClassName,
  intro,
}) {
  // Read the rate card at render time: it is replaced in place when the live
  // catalog loads.
  const planOrder = (planOrderProp || PUBLIC_PLAN_ORDER).filter(
    (pid) => PLANS[pid]
  );
  const addOnList = showAddOns ? globalAddOns() : [];
  const cmpRows = showCompare ? compareRows(planOrder) : [];

  return (
    <section id={id} className="nv-section nv-pricing nv-center">
      <div className="nv-wrap">
        <div className="nv-eyebrow">{eyebrow}</div>
        <h2 className="nv-sec-title">{title}</h2>
        {subtitle ? <p className="nv-sec-sub">{subtitle}</p> : null}
        {showFreeTrial && (
          <div className="nv-free-pill">
            <span role="img" aria-label="gift">
              🎁
            </span>{' '}
            First month FREE on all plans
          </div>
        )}
        {intro}
        <div className="nv-px-grid">
          {planOrder.map((pid) => {
            const p = PLANS[pid];
            const extraClass = cardClassName ? cardClassName(pid, p) : '';
            return (
              <div
                key={pid}
                className={`nv-px ${p.badge ? 'nv-pop' : ''} ${
                  extraClass || ''
                }`}
                style={{ '--plan-color': p.color }}
              >
                <div className="nv-px-name">
                  {p.name}
                  {p.badge && <span className="nv-px-pop">★ {p.badge}</span>}
                  {renderNameBadge ? renderNameBadge(pid, p) : null}
                </div>
                {(renderPrice && renderPrice(pid, p)) || (
                  <div className="nv-px-price">
                    <span className="nv-amt">${p.price}</span>
                    <span className="nv-per">/mo</span>
                  </div>
                )}
                {renderPriceNote ? renderPriceNote(pid, p) : null}
                {renderAction ? (
                  renderAction(pid, p)
                ) : (
                  <button
                    className={`nv-btn ${
                      p.badge ? 'nv-btn-primary' : 'nv-btn-blue'
                    }`}
                    onClick={() => onGetStarted && onGetStarted(pid)}
                  >
                    Get Started
                  </button>
                )}
                {p.sections.map((sec) => (
                  <div className="nv-px-grp" key={sec.title}>
                    <h4>{sec.title}</h4>
                    {sec.note ? (
                      <div className="nv-all">{sec.note}</div>
                    ) : (
                      <ul>
                        {(sec.items || []).map((it) => (
                          <li key={it}>{it}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {showAddOns && (
          <div style={{ marginTop: 56 }}>
            <h2
              className="nv-sec-title"
              style={{ fontSize: 'clamp(20px,3vw,30px)' }}
            >
              Optional Add-Ons
            </h2>
            <p className="nv-sec-sub">
              Extend a lower tier with the modules you need — higher plans
              already include them.
            </p>
            <div className="nv-addons">
              {addOnList.map((a) => (
                <div className="nv-ad" key={a.id}>
                  <b>{a.name}</b>
                  <div className="nv-ad-p">+${a.price}/mo</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showFreeTrial && (
          <p className="nv-px-foot">
            First month free on all plans · billed monthly · no setup fees ·
            cancel anytime.
          </p>
        )}

        {/* Compare Plans */}
        {showCompare && (
          <>
            <div style={{ marginTop: 56 }}>
              <h2
                className="nv-sec-title"
                style={{ fontSize: 'clamp(20px,3vw,30px)' }}
              >
                Compare Plans
              </h2>
            </div>
            <div className="nv-cmp-wrap">
              <table className="nv-cmp">
                <thead>
                  <tr>
                    <th>Feature</th>
                    {planOrder.map((pid) => (
                      <th
                        key={pid}
                        className={
                          PLANS[pid]?.badge === 'POPULAR'
                            ? 'nv-pop-th'
                            : undefined
                        }
                      >
                        {PLANS[pid]?.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cmpRows.map((row) => (
                    <tr key={row.f}>
                      <td>{row.f}</td>
                      {row.cells.map((cell, i) => (
                        <td
                          key={planOrder[i]}
                          className={
                            PLANS[planOrder[i]]?.badge === 'POPULAR'
                              ? 'nv-popcell'
                              : undefined
                          }
                        >
                          {renderCmpCell(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
